/**
 * Typed fetchers for /api/v1/integration/connections — the connector
 * console's CRUD + test surface (M3).
 *
 * Types come from the OpenAPI-generated schema. Regenerate via
 * `pnpm gen:api` after a backend Pydantic change. Secrets are write-only:
 * `credential` is sent on create / rotate but never returned — responses
 * expose `has_credential` only (FS-903 / URS-819).
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

export type ConnectionOut = components["schemas"]["ConnectionOut"];
export type ConnectionListOut = components["schemas"]["ConnectionListOut"];
export type CatalogEntry = components["schemas"]["CatalogEntryOut"];
export type CatalogOut = components["schemas"]["CatalogOut"];
export type CreateConnectionIn = components["schemas"]["CreateConnectionIn"];
export type UpdateConnectionIn = components["schemas"]["UpdateConnectionIn"];
export type TestConnectionIn = components["schemas"]["TestConnectionIn"];
export type TestConnectionOut = components["schemas"]["TestConnectionOut"];

/** Source types the console can create today (ADR-0009 — extensible). */
export const SOURCE_TYPES = [
  "veeva",
  "blob",
  "s3",
  "ai_foundry",
  "azure_devops",
  "database",
  "cara",
] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];

/** Field-level classification (ADR-0002 / URS-820). */
export const CLASSIFICATIONS = ["public", "confidential", "pii", "phi"] as const;

/** The catalog of source types — `available` (connectable) or `coming_soon`. */
export function listCatalog(token: string): Promise<CatalogOut> {
  return apiFetch<CatalogOut>("/api/v1/integration/catalog", { token });
}

export function listConnections(
  token: string,
  sourceType?: string | null,
): Promise<ConnectionListOut> {
  const qs = sourceType ? `?source_type=${encodeURIComponent(sourceType)}` : "";
  return apiFetch<ConnectionListOut>(`/api/v1/integration/connections${qs}`, { token });
}

export function getConnection(token: string, connectionId: string): Promise<ConnectionOut> {
  return apiFetch<ConnectionOut>(
    `/api/v1/integration/connections/${encodeURIComponent(connectionId)}`,
    { token },
  );
}

export function createConnection(token: string, input: CreateConnectionIn): Promise<ConnectionOut> {
  return apiFetch<ConnectionOut>("/api/v1/integration/connections", {
    method: "POST",
    token,
    json: input,
  });
}

export function updateConnection(
  token: string,
  connectionId: string,
  input: UpdateConnectionIn,
): Promise<ConnectionOut> {
  return apiFetch<ConnectionOut>(
    `/api/v1/integration/connections/${encodeURIComponent(connectionId)}`,
    { method: "PATCH", token, json: input },
  );
}

export function deleteConnection(
  token: string,
  connectionId: string,
  reason: string,
): Promise<void> {
  const qs = `?reason=${encodeURIComponent(reason)}`;
  return apiFetch<void>(
    `/api/v1/integration/connections/${encodeURIComponent(connectionId)}${qs}`,
    { method: "DELETE", token },
  );
}

/** Probe an unsaved config before saving (FS-902). Returns a verdict. */
export function testAdhocConnection(
  token: string,
  input: TestConnectionIn,
): Promise<TestConnectionOut> {
  return apiFetch<TestConnectionOut>("/api/v1/integration/connections/test", {
    method: "POST",
    token,
    json: input,
  });
}

/** Probe a saved connection (FS-902). Updates its health server-side. */
export function testSavedConnection(
  token: string,
  connectionId: string,
): Promise<TestConnectionOut> {
  return apiFetch<TestConnectionOut>(
    `/api/v1/integration/connections/${encodeURIComponent(connectionId)}/test`,
    { method: "POST", token },
  );
}

/**
 * Auth modes that carry no stored credential — access comes from an identity
 * instead. Mirrors `CREDENTIALLESS_AUTH_MODES` in
 * `services/connection_resolver.py`; keep the two in step.
 */
const CREDENTIALLESS_AUTH_MODES = new Set<string>([
  "managed_identity",
  "entra_token",
  // S3 OIDC federation: the token is minted from the platform's own managed
  // identity at call time, so there is nothing to store (ADR-0021 §2).
  "oidc_web_identity",
]);

/** Whether a connection in this auth mode has a credential at all. */
export function authModeNeedsCredential(authMode: string): boolean {
  return !CREDENTIALLESS_AUTH_MODES.has(authMode);
}

/**
 * What this connection's credential is actually called, so the UI never asks for
 * a "credential" generically — a Veeva connection wants a password, an AI
 * Foundry one wants an API key, and calling either the other is confusing at the
 * moment the operator is looking for it in the Azure portal.
 */
export function credentialLabel(sourceType: string, authMode: string): string {
  if (sourceType === "ai_foundry") return "API key";
  if (sourceType === "blob") return "Connection string / SAS";
  if (sourceType === "database") return "Database password";
  if (sourceType === "cara") return "API token";
  if (sourceType === "s3") return "Secret access key";
  if (authMode === "oauth2") return "OAuth2 access token";
  return "Service-account password";
}

/**
 * The single credential string an S3 connection stores.
 *
 * S3 needs an access key *pair*, but a connection holds exactly one Key Vault
 * secret (ADR-0007), so the two fields travel as one JSON value — the shape
 * `connection_resolver._s3_key_pair` parses. Both the create form and the
 * rotate form compose it here, so they cannot drift into two shapes.
 */
export function s3Credential(accessKeyId: string, secretAccessKey: string): string {
  return JSON.stringify({
    access_key_id: accessKeyId.trim(),
    secret_access_key: secretAccessKey,
  });
}

/**
 * Source types that have a data browser at `/integration/<source_type>/<id>`.
 *
 * An allow-list rather than a "not AI" check: a new connector without a browser
 * would otherwise inherit a Browse button that routes nowhere. Keep this in step
 * with the routes in `app/router.tsx`.
 */
const BROWSABLE_SOURCE_TYPES = new Set<string>(["veeva", "blob", "s3", "database", "cara"]);

/** Whether this connection has a browser to link to. */
export function hasBrowser(sourceType: string): boolean {
  return BROWSABLE_SOURCE_TYPES.has(sourceType);
}

/** Badge colour for a connection's health status. */
/**
 * Re-exported from the semantic palette so callers keep importing it from the
 * API module they already use. The mapping itself lives in one place
 * (`shared/ui/palette.ts`) — note `expired` is **yellow** there, not the orange
 * it used to be here: orange now means "target node" and a colour cannot mean
 * two things.
 */
export { healthColor } from "@/shared/ui/palette";

export type BindingOut = components["schemas"]["BindingOut"];
export type BindingListOut = components["schemas"]["BindingListOut"];

/** Every `@role` bound in this deployment, and the connection it points at.
 *
 *  Readable by anyone who may see connections — knowing that `@source_vault`
 *  means the sandbox is how a pipeline author understands what their own
 *  pipeline will do. Changing one is Administrator-only. */
export function listConnectionBindings(token: string): Promise<BindingListOut> {
  return apiFetch<BindingListOut>("/api/v1/integration/connection-bindings", { token });
}

export function setConnectionBinding(
  token: string,
  role: string,
  input: { connection_id: string; note?: string },
): Promise<BindingOut> {
  return apiFetch<BindingOut>(
    `/api/v1/integration/connection-bindings/${encodeURIComponent(role)}`,
    { method: "PUT", token, json: input },
  );
}

export function removeConnectionBinding(token: string, role: string): Promise<void> {
  return apiFetch<void>(`/api/v1/integration/connection-bindings/${encodeURIComponent(role)}`, {
    method: "DELETE",
    token,
  });
}
